import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BookOpen } from 'lucide-react';
import { usePlanSynthesis } from '@/hooks/usePlanSynthesis';
import { PlanAssemblyIntro } from './PlanAssemblyIntro';
import { PlanAssemblyLevelSet } from './PlanAssemblyLevelSet';

type Step = 'intro' | 'level-set';

interface PlanAssemblyFlowProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export function PlanAssemblyFlow({ open, onClose, onComplete }: PlanAssemblyFlowProps) {
  const [step, setStep] = useState<Step>('intro');
  const { planData, isLoading } = usePlanSynthesis();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard-assessment-modules'] });
    queryClient.invalidateQueries({ queryKey: ['userScores'] });
    queryClient.invalidateQueries({ queryKey: ['succession-plan'] });
    onComplete?.();
    onClose();
    navigate('/plan');
  }, [queryClient, onComplete, onClose, navigate]);

  const stepTitle = step === 'intro' ? 'Plan Assembly' : 'Your Assessment Summary';

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="w-full max-w-4xl h-[80vh] flex flex-col p-0 rounded-xl overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{stepTitle}</DialogTitle>
          <DialogDescription>Build your personalized succession plan</DialogDescription>
        </DialogHeader>

        {/* Header bar — matches AssessmentOverlay */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="font-medium text-[15px] text-foreground">mySuccession Plan</span>
          </div>
        </div>

        {/* Screen content */}
        <div className="h-full flex flex-col overflow-hidden">
          {step === 'intro' && (
            <PlanAssemblyIntro
              onNext={() => setStep('level-set')}
              onClose={onClose}
            />
          )}
          {step === 'level-set' && (
            <PlanAssemblyLevelSet
              planData={planData}
              isLoading={isLoading}
              onComplete={handleComplete}
              onClose={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
